<template>
  <article class="content mt-lg-4">
    <div class="col-12">
      <div class="blog-meta d-flex gap-3 gap-lg-8 flex-wrap mb-5">
        <div class="published mb-lg-0">
          <span class="d-block text-preset-details-small mb-2">{{
            $t('Published')
          }}</span>
          <span class="d-block body-2-regular">{{ date }}</span>
        </div>
        <div class="author mb-lg-0">
          <span class="d-block text-preset-details-small mb-2">{{
            $tc('Authors', authors.length)
          }}</span>
          <span class="description d-block body-2-regular">
            <template v-if="authors.length">
              <span v-for="(author, i) in authors" :key="author.title">
                <T3Link :href="author.detailPage" v-if="author.detailPage">
                  <template #content>
                    {{ author.firstname }} {{ author.lastname
                    }}<template v-if="i + 1 < authors.length">, </template>
                  </template>
                </T3Link>
                <template v-else>
                  {{ author.firstname }} {{ author.lastname
                  }}<template v-if="i + 1 < authors.length">, </template>
                </template>
              </span>
            </template>
            <template v-else> TISSO Naturprodukte GmbH </template>
          </span>
        </div>
        <div class="read mb-lg-0">
          <span class="d-block text-preset-details-small mb-2">{{
            $t('Read time')
          }}</span>
          <span class="d-block body-2-regular">{{ readTime }}</span>
        </div>
      </div>
      <h1 class="h2">
        {{ title }}
      </h1>

      <!-- Jump Links Navigation -->
      <div v-if="jumpLinks && jumpLinks.length" class="jump-links-container">
        <h2 class="jump-links-heading">{{ $t('Table of Contents') }}</h2>
        <ul class="jump-links-list">
          <li
            v-for="link in jumpLinks"
            :key="link.id"
            :class="{ 'level-3': link.level === 3 }"
          >
            <a href="javascript:void(0)" @click="scrollTo(link.link)">
              {{ link.title }}
            </a>
          </li>
        </ul>
      </div>
    </div>
    <span v-html="getBodytext()" />
  </article>
</template>

<script>
import RatioImage from './RatioImage.vue';
import GridElement from '~/components/GridElement.vue';
import DynamicElement from '~/components/DynamicElement.vue';
import T3Link from '~/components/General/T3Link.vue';
import { scrollTo } from 'vue-scrollto';

export default {
  name: 'MainContentBlogDetail',
  components: {
    T3Link,
    RatioImage,
    GridElement,
    DynamicElement,
  },
  props: {
    bodytext: String,
    date: Date | String,
    content: Array,
    title: String,
    readTime: String,
    authors: Array,
    jumpLinks: Array,
  },
  methods: {
    getBodytext() {
      const bodytext = this.bodytext;
      return bodytext.replace(
        'src="fileadmin',
        'src="' + this.basePath + '/fileadmin',
      );
    },
    scrollTo(link) {
      // Extract the ID from the link (e.g., "#c17206")
      const elementId = link.replace('#', '');
      const targetElement = document.getElementById(elementId);

      if (targetElement) {
        scrollTo(targetElement, {
          duration: 600,
          offset: -20,
          easing: 'ease-in-out',
          onDone: () => {
            // Update URL hash without triggering another scroll
            history.pushState(null, null, link);
          },
        });
      } else {
        console.warn(`Element with ID ${elementId} not found`);
      }
    },
  },
  data() {
    return {
      basePath: process.env.T3_API_BASE,
    };
  },
};
</script>

<style lang="scss" scoped>
@import 'bootstrap/scss/functions';
@import 'bootstrap/scss/variables';
@import 'bootstrap/scss/mixins';

@import 'assets/styles/misc/vars';
.content {
  h4,
  .h4 {
    margin-bottom: var(--spacer-5);
  }
  .d-grid {
    gap: var(--spacer-2);
  }
}

/* Jump Links Styling */
.jump-links-container {
  overflow-x: auto;
  border-bottom: 1px solid #e5e5e5;
  margin: 1.5rem -15px 2rem;
  padding: 0 15px;
  -webkit-overflow-scrolling: touch;
}

.jump-links-heading {
  font-size: 1rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #3e3e3f;
  padding: 0 0.75rem;
}

.jump-links-list {
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0 0 0.5rem;
  margin: 0;

  li {
    margin: 0;

    &.level-3 {
      padding-left: 1.25rem;

      a {
        font-size: 1rem;
        padding: 0.25rem 0.75rem;
      }
    }

    a {
      display: inline-flex;
      align-items: center;
      padding: 0.3rem 0.75rem;
      color: #6f7953;
      font-size: 1rem;
      text-decoration: none;
      transition: all 0.2s ease;

      &:hover,
      &:focus {
        background-color: rgba(111, 121, 83, 0.07);
        text-decoration: none;
      }

      .arrow-icon {
        width: 10px;
        height: 10px;
        margin-left: 5px;
        margin-top: 1px;
        stroke-width: 2.5;
      }
    }
  }
}

@include media-breakpoint-up(md) {
  .d-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@include media-breakpoint-up(lg) {
  .blog-meta {
    gap: var(--spacer-5);
    .published,
    .read {
      flex: 0 0 max-content;
    }
  }
  .ratio {
    --bs-aspect-ratio: 30.75%;
  }
}
</style>
